import { CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { html } from 'lit/static-html.js';

import componentStyles from './sic-cta.css.lit';
import defaultVars from './css-variables/vars-default.css.lit';

import { wrapLastWord } from '../../../utilities/wrap-last-word';
import { checkFileType } from '../../../utilities/check-file-type';
import { externalLinkText } from '../../../utilities/external-link-text';

export type FileIcon = 'pdf' | 'file';

/**
 * Type for setting CTA size.
 *
 * This type currently only includes utilized values, but should be
 * expanded to include other variants as needed.
 * Non-locked sizes use the project's default fluid typography.
 * Locked sizes provide a fixed size across all breakpoints.
 */
export type CtaSize = 'sm-locked' | 'normal' | 'large';

/**
 * The CTA component.
 * @element sic-CTA
 * @slot defaultSlot
 *
 * @prop is-external
 * @prop default-icon
 * @prop decorative-icon
 * @prop file-type
 * @prop multi-line
 * @prop size
 *
 * Lightdom styles in sic-cta.lightdom.css included in /shared.css
 */
@customElement('sic-cta')
export class SicCta extends OutlineElement {
  static styles: CSSResultGroup = [defaultVars, componentStyles];

  /**
   * Whether the link is External.
   */
  @property({ type: Boolean, attribute: 'is-external' })
  isExternal = false;

  /**
   * Default Icon.
   */
  @property({ type: Boolean, attribute: 'default-icon' })
  defaultIcon = true;

  /**
   * Sets the generated icon to decorative for accessibility.
   */
  @property({ type: Boolean, attribute: 'decorative-icon' })
  decorativeIcon = true;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'file-type' })
  fileType: FileIcon | undefined;

  /**
   * Multi line.
   */
  @property({ type: Boolean, attribute: 'multi-line' })
  multiLine = true;

  /**
   * Size.
   */
  @property({ type: String, attribute: 'size' })
  size: CtaSize = 'normal';

  /**
   * Aria-label for accessibility.
   * This can be set manually or will be auto-generated based on placement.
   */
  @property({ type: String, attribute: 'aria-label' })
  ariaLabel: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.setModifierClasses();

    this?.querySelector('a')?.classList.add('sic-cta');
    this?.querySelector('button')?.classList.add('sic-cta');

    this.querySelectorAll('.sic-cta').forEach(el => {
      checkFileType(el as HTMLElement);
      // Wraps last word in a span.last-word element.
      // span.last-word is where the icon is added via pseudo
      wrapLastWord(el as HTMLElement);

      // Adds external link text for screen readers.
      if (
        el.getAttribute('target') === '_blank' ||
        el.classList.contains('is-external-link')
      ) {
        externalLinkText(el as HTMLElement);
      }
    });
  }

  /**
   * Sets an appropriate aria-label if not manually provided.
   * Called via slotchange so the interactive element is guaranteed present.
   */
  private setAriaLabel(): void {
    const element = this.querySelector('a') || this.querySelector('button');

    if (!element) {
      return;
    }

    if (this.ariaLabel !== null) {
      element.setAttribute('aria-label', this.ariaLabel);
    } else {
      const text = this.innerText.trim();
      const parentTag = this.closest('header')
        ? 'Header'
        : this.closest('footer')
        ? 'Footer'
        : 'Content';

      const computedAriaLabel = text
        ? `${text} in ${parentTag}`
        : `CTA in ${parentTag}`;
      element.setAttribute('aria-label', computedAriaLabel);
    }
  }

  private _onSlotChange(): void {
    this.setAriaLabel();
  }

  updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);
    if (_changedProperties.has('ariaLabel')) {
      this.setAriaLabel();
    }
  }

  render(): TemplateResult {
    return html`<div><slot @slotchange=${this._onSlotChange}></slot></div>`;
  }

  setModifierClasses() {
    const element = this.querySelector('a');
    const target = element?.getAttribute('target');

    const external = target === '_blank' || this.isExternal;

    if (external) {
      element?.classList.add('icon-external');
      element?.classList.add('is-external-link');
    } else {
      element?.classList.add('icon-default');
    }

    if (this.multiLine) {
      element?.classList.add('multi-line');
    }

    element?.classList.add(this.size);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-cta': SicCta;
  }
}
